import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  createAdminUser,
  deleteAdminUser,
  fetchAdminUsers,
  resendAdminInvitation,
  updateAdminUser,
} from "../../api/adminUserApi";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import { resolveApiError } from "../../utils/apiError";
import "./users.scss";

const SORT_OPTIONS = [
  { value: "created_at", label: "Created date" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "status", label: "Status" },
  { value: "last_active", label: "Last active" },
];

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  invited: "Invited",
};

const formatLast = (value) =>
  value
    ? new Date(value).toLocaleString("en-MY", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Never";

const initials = (name = "") =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0, invited: 0 });
  const [meta, setMeta] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminUsers({
        search: debouncedQuery || undefined,
        status: status || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        per_page: perPage,
      });
      setUsers(data.users);
      setMeta(data.meta);
      setSummary(data.summary || { total: 0, active: 0, inactive: 0, invited: 0 });
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, status, sortBy, sortOrder, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  const resetFilters = () => {
    setQuery("");
    setDebouncedQuery("");
    setStatus("");
    setSortBy("created_at");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  const invite = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const phone = String(data.get("phone") || "").trim();
    setSaving(true);
    setError("");
    try {
      const result = await createAdminUser({
        name: data.get("name"),
        email: data.get("email"),
        ...(phone ? { phone } : {}),
      });
      setOpen(false);
      setMessage(
        result?.invitation_sent === false
          ? "Account created, but the invitation email could not be sent. Use Resend invitation."
          : "Invitation sent. The administrator will set their password from the email link."
      );
      await load();
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setSaving(false);
    }
  };

  const saveUser = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const phone = String(data.get("phone") || "").trim();
    const nextStatus =
      editingUser.status === "invited" ? "invited" : data.get("status") || editingUser.status;
    setSaving(true);
    setError("");
    try {
      await updateAdminUser(editingUser.id, {
        name: data.get("name"),
        email: data.get("email"),
        phone: phone || null,
        status: nextStatus,
      });
      setEditingUser(null);
      setMessage("Administrator account updated.");
      await load();
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async () => {
    if (!editingUser) return;
    setResending(true);
    setError("");
    try {
      await resendAdminInvitation(editingUser.id);
      setMessage(`Invitation resent to ${editingUser.email}.`);
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setResending(false);
    }
  };

  const remove = async () => {
    if (!editingUser) return;
    setSaving(true);
    setError("");
    try {
      await deleteAdminUser(editingUser.id);
      setConfirmDelete(false);
      setEditingUser(null);
      setMessage("Administrator account deleted.");
      await load();
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setSaving(false);
    }
  };

  const activeFilterCount = status ? 1 : 0;

  return (
    <div className="users-page">
      {message && (
        <PageAlert variant="success" onDismiss={() => setMessage("")}>{message}</PageAlert>
      )}
      {error && (
        <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>
      )}
      <section className="users-page__toolbar">
        <div>
          <h2>Portal users</h2>
          <p>Manage administrator accounts for the portal.</p>
        </div>
        <button className="ui-button ui-button--primary" type="button" onClick={() => setOpen(true)}>
          <Icon icon="ph:plus" height={14} /> Invite user
        </button>
      </section>
      <section className="users-page__layout">
        <article className="users-page__card">
          <div className="users-page__card-head">
            <div>
              <p>ADMINISTRATORS</p>
              <h3>{summary.total ?? meta?.total ?? 0} portal users</h3>
            </div>
            <span>{summary.active ?? 0} active</span>
          </div>

          <ModuleSearchControls
            value={query}
            onChange={setQuery}
            placeholder="Search name, email or phone"
          >
            <ModuleFilterMenu
              open={filterOpen}
              onToggle={() => {
                setFilterOpen((value) => !value);
                setSortOpen(false);
                setPerPageOpen(false);
              }}
              onClose={() => setFilterOpen(false)}
              count={activeFilterCount}
              columns={1}
              size="narrow"
              ariaLabel="User filters"
            >
              <fieldset>
                <legend>Status</legend>
                {[
                  ["", "All statuses"],
                  ["active", "Active"],
                  ["inactive", "Inactive"],
                  ["invited", "Invited"],
                ].map(([value, label]) => (
                  <ModuleRadioOption
                    key={value || "all"}
                    name="user-status"
                    checked={status === value}
                    onChange={() => {
                      setStatus(value);
                      setPage(1);
                    }}
                    plain
                  >
                    {label}
                  </ModuleRadioOption>
                ))}
              </fieldset>
            </ModuleFilterMenu>
            <ModuleSortMenu
              open={sortOpen}
              onToggle={() => {
                setSortOpen((value) => !value);
                setFilterOpen(false);
                setPerPageOpen(false);
              }}
              onClose={() => setSortOpen(false)}
              sortBy={sortBy}
              sortOrder={sortOrder}
              options={SORT_OPTIONS}
              onChangeSortBy={(value) => {
                setSortBy(value);
                setPage(1);
              }}
              onChangeSortOrder={(value) => {
                setSortOrder(value);
                setPage(1);
              }}
            />
            <ModulePerPageMenu
              open={perPageOpen}
              onToggle={() => {
                setPerPageOpen((value) => !value);
                setFilterOpen(false);
                setSortOpen(false);
              }}
              onClose={() => setPerPageOpen(false)}
              value={perPage}
              onChange={(value) => {
                setPerPage(value);
                setPage(1);
              }}
            />
            <button className="ui-button" type="button" onClick={resetFilters}>
              <Icon icon="ph:arrow-clockwise" height={14} /> Reset
            </button>
          </ModuleSearchControls>

          <DataTable
            loading={loading}
            loadingMessage="Loading users…"
            emptyMessage="No administrators found."
            rows={users}
            columns={[
              {
                key: "user",
                header: "User",
                render: (user) => (
                  <div className="users-page__person">
                    <span>{initials(user.name)}</span>
                    <div>
                      <strong>{user.name}</strong>
                      <small>{user.email}</small>
                      {user.phone ? <small>{user.phone}</small> : null}
                    </div>
                  </div>
                ),
              },
              {
                key: "role",
                header: "Role",
                render: (user) => <span className="users-page__role-label">{user.role_name}</span>,
              },
              {
                key: "status",
                header: "Status",
                render: (user) => (
                  <span className={`users-page__status users-page__status--${user.status}`}>
                    {STATUS_LABELS[user.status] || user.status}
                  </span>
                ),
              },
              {
                key: "last_active",
                header: "Last active",
                render: (user) => <small>{formatLast(user.last_active)}</small>,
              },
              {
                key: "actions",
                header: "",
                render: (user) =>
                  user.is_current ? (
                    <span className="users-page__protected">Current account</span>
                  ) : (
                    <DataTableActions>
                      <button type="button" onClick={() => setEditingUser(user)}>
                        Edit
                      </button>
                    </DataTableActions>
                  ),
              },
            ]}
          />
          <DataTableFooter meta={meta} loading={loading} onPageChange={setPage} noun="users" />
        </article>
      </section>

      {open && (
        <div className="modal">
          <form className="modal__card" onSubmit={invite}>
            <div className="modal__head">
              <div>
                <p>NEW ADMINISTRATOR</p>
                <h3>Invite portal user</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close">
                <Icon icon="ph:x" height={16} />
              </button>
            </div>
            <label>
              Full name
              <input name="name" required />
            </label>
            <label>
              Email address
              <input name="email" required type="email" />
            </label>
            <label>
              Phone <span>(optional)</span>
              <input name="phone" type="tel" placeholder="+60123456789" />
            </label>
            <div className="users-page__account-note">
              An invitation email will be sent so they can set their own password. The account stays
              invited until they accept.
            </div>
            <div className="modal__actions">
              <button type="button" className="ui-button" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button className="ui-button ui-button--primary" disabled={saving}>
                {saving ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </form>
        </div>
      )}

      {editingUser && (
        <div className="modal">
          <form
            className={`modal__card ${editingUser.status === "invited" ? "modal__card--invited-edit" : ""}`}
            onSubmit={saveUser}
          >
            <div className="modal__head">
              <div>
                <p>ADMINISTRATOR ACCOUNT</p>
                <h3>Edit portal user</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  setEditingUser(null);
                }}
                aria-label="Close"
              >
                <Icon icon="ph:x" height={16} />
              </button>
            </div>
            <label>
              Full name
              <input name="name" required defaultValue={editingUser.name} />
            </label>
            <label>
              Email address
              <input name="email" required type="email" defaultValue={editingUser.email} />
            </label>
            <label>
              Phone <span>(optional)</span>
              <input name="phone" type="tel" defaultValue={editingUser.phone || ""} placeholder="+60123456789" />
            </label>
            {editingUser.status === "invited" ? (
              <>
                <label>
                  Account status
                  <input value="Invited" disabled readOnly />
                </label>
                <div className="users-page__account-note">
                  Invited accounts stay invited until the invitation is accepted.
                </div>
              </>
            ) : (
              <label>
                Account status
                <select name="status" defaultValue={editingUser.status}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            )}
            <div
              className={`modal__actions ${
                editingUser.status === "invited" ? "modal__actions--invited" : ""
              }`}
            >
              <button type="button" className="users-page__delete" onClick={() => setConfirmDelete(true)} disabled={saving}>
                Delete account
              </button>
              {editingUser.status === "invited" && (
                <button type="button" className="ui-button" onClick={resendInvite} disabled={resending}>
                  {resending ? "Resending…" : "Resend invitation"}
                </button>
              )}
              <button type="button" className="ui-button" onClick={() => {
                setConfirmDelete(false);
                setEditingUser(null);
              }}>
                Cancel
              </button>
              <button className="ui-button ui-button--primary" disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(confirmDelete && editingUser)}
        title="Delete account"
        message={
          editingUser
            ? `Delete ${editingUser.name}'s administrator account? This cannot be undone.`
            : ""
        }
        confirming={saving}
        onCancel={() => !saving && setConfirmDelete(false)}
        onConfirm={remove}
      />
    </div>
  );
};

export default UsersPage;
